/**
 * Audio playback preferences: skip length on each side, playback rate kept
 * per book, and the three post-processing toggles the audio chain will read.
 *
 * Plain TypeScript, no React, so the settings menu, the player bar, and the
 * audio chain can all read and write without importing one another.
 *
 * Persisted, because a listener sets a skip length or a book's speed once and
 * expects it to hold. The defaults reproduce today's hardcoded behaviour
 * exactly (-15/+30, 1x, nothing processed), so nobody's playback changes
 * until they open this menu on purpose.
 */

import { Player } from "./player";

export interface AudioPrefs {
  /** Seconds jumped back by the rewind control. */
  skipBack: number;
  /** Seconds jumped forward by the fast forward control. */
  skipForward: number;
  /** Playback rate remembered per book id. A book with no entry plays at 1x. */
  rateByBook: Record<string, number>;
  /** Voice boost in the audio chain. */
  boost: boolean;
  /** Loudness normalisation in the audio chain. */
  normalize: boolean;
  /** Silence trimming in the audio chain. */
  trimSilence: boolean;
}

const KEY = "readcommons:audio-prefs";

/** The only skip lengths offered. Each side picks independently among these. */
export const SKIP_STEPS: readonly number[] = [5, 10, 15, 20, 30, 45, 60];

const DEFAULTS: AudioPrefs = {
  skipBack: 15,
  skipForward: 30,
  rateByBook: {},
  boost: false,
  normalize: false,
  trimSilence: false,
};

type Listener = (prefs: AudioPrefs) => void;

const listeners = new Set<Listener>();

function closestOf(options: readonly number[], value: unknown, fallback: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  return options.reduce((best, option) =>
    Math.abs(option - value) < Math.abs(best - value) ? option : best,
  options[0]);
}

/** Snap a rate onto one of the values `player.ts` actually offers. */
function closestRate(value: unknown, fallback: number): number {
  return closestOf(Player.rates, value, fallback);
}

function sanitizeRateByBook(value: unknown): Record<string, number> {
  if (typeof value !== "object" || value === null) return {};
  const out: Record<string, number> = {};
  for (const [bookId, rate] of Object.entries(value as Record<string, unknown>)) {
    if (typeof rate === "number" && Number.isFinite(rate)) out[bookId] = closestRate(rate, 1);
  }
  return out;
}

function read(): AudioPrefs {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULTS;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return DEFAULTS;
    const value = parsed as Partial<AudioPrefs>;
    return {
      skipBack: closestOf(SKIP_STEPS, value.skipBack, DEFAULTS.skipBack),
      skipForward: closestOf(SKIP_STEPS, value.skipForward, DEFAULTS.skipForward),
      rateByBook: sanitizeRateByBook(value.rateByBook),
      boost: value.boost === true,
      normalize: value.normalize === true,
      trimSilence: value.trimSilence === true,
    };
  } catch {
    // Corrupt JSON, or storage denied by the browser: act as if nothing was saved.
    return DEFAULTS;
  }
}

function write(next: AudioPrefs): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Private browsing. The session still works, it just will not be remembered.
  }
}

function notify(): void {
  for (const listener of listeners) listener(state);
}

let state: AudioPrefs = read();

export function loadAudioPrefs(): AudioPrefs {
  return state;
}

export function saveAudioPrefs(patch: Partial<AudioPrefs>): void {
  const skipBack =
    patch.skipBack !== undefined ? closestOf(SKIP_STEPS, patch.skipBack, state.skipBack) : state.skipBack;
  const skipForward =
    patch.skipForward !== undefined
      ? closestOf(SKIP_STEPS, patch.skipForward, state.skipForward)
      : state.skipForward;
  state = { ...state, ...patch, skipBack, skipForward };
  write(state);
  notify();
}

/** The rate remembered for one book, or 1x when it has never been set. */
export function rateFor(bookId: string): number {
  return state.rateByBook[bookId] ?? 1;
}

export function setRateFor(bookId: string, rate: number): void {
  const bounded = closestRate(rate, 1);
  state = { ...state, rateByBook: { ...state.rateByBook, [bookId]: bounded } };
  write(state);
  notify();
}

/** Called once immediately with the current value, like `Player.subscribe`. */
export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  listener(state);
  return () => {
    listeners.delete(listener);
  };
}
